// /app/frontend/src/components/frugal-v2/tabs/ExecutionTab.jsx
// Budget execution by categories (150 lines)

import React, { useState, useEffect } from 'react';
import { PieChart, Eye, EyeOff } from 'lucide-react';
import { CATEGORY_COLORS } from '../constants/colors';
import CategoryRow from './execution/CategoryRow';
import * as api from '../services/frugalApi';

const SORT_OPTIONS = [
  { value: 'spent', label: 'sortBySpent' },
  { value: 'budget', label: 'sortByBudget' },
  { value: 'percent', label: 'sortByPercent' },
  { value: 'alpha', label: 'sortByName' }
];

const ExecutionTab = ({ token, t }) => {
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideZeros, setHideZeros] = useState(true);
  const [sortBy, setSortBy] = useState('spent');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryTransactions, setCategoryTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState(null);

  useEffect(() => {
    const fetchExecution = async () => {
      try {
        const data = await api.fetchBudgetExecution(token);
        setExecution(data);
      } catch (error) {
        console.error('Error fetching budget execution:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchExecution();
  }, [token]);

  const toggleCategory = async (category) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
      return;
    }
    setExpandedCategory(category);
    if (!categoryTransactions[category]) {
      setLoadingTransactions(category);
      try {
        const data = await api.fetchCategoryTransactions(token, category);
        setCategoryTransactions(prev => ({ ...prev, [category]: data.transactions || [] }));
      } catch {
        setCategoryTransactions(prev => ({ ...prev, [category]: [] }));
      } finally {
        setLoadingTransactions(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-emerald-400">{t('frugal.status.loading')}</div>
      </div>
    );
  }

  const categories = execution?.categories || [];
  
  const sortCategories = (cats) => {
    const sorted = [...cats];
    switch (sortBy) {
      case 'spent': return sorted.sort((a, b) => b.spent - a.spent);
      case 'budget': return sorted.sort((a, b) => b.budget - a.budget);
      case 'percent': return sorted.sort((a, b) => b.percent - a.percent);
      case 'alpha': return sorted.sort((a, b) => a.category.localeCompare(b.category));
      default: return sorted;
    }
  };
  
  const filteredCategories = hideZeros ? categories.filter(c => c.budget > 0 || c.spent > 0) : categories;
  const displayCategories = sortCategories(filteredCategories);

  const totalBudget = execution?.total_budget || 0;
  const totalSpent = execution?.total_spent || 0;
  const daysElapsed = execution?.days_elapsed || 1;
  const daysTotal = execution?.days_total || 30;

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PieChart className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-white">{t('frugal.execution.title')}</h2>
        </div>
        <div className="flex items-center gap-3">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="bg-white/10 text-sm text-slate-300 rounded-lg px-2 py-1 border border-white/10 focus:outline-none">
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} className="bg-slate-800">
                {t(`frugal.execution.${opt.label}`)}
              </option>
            ))}
          </select>
          <button onClick={() => setHideZeros(!hideZeros)}
            className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
            {hideZeros ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {hideZeros ? t('frugal.budget.showAll') : t('frugal.budget.hideZeros')}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-xs text-slate-400 mb-1">{t('frugal.budget.budgeted')}</div>
            <div className="text-lg font-bold text-white">${totalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">{t('frugal.execution.spent')}</div>
            <div className="text-lg font-bold text-emerald-400">${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">{t('frugal.execution.progress')}</div>
            <div className="text-lg font-bold text-white">{daysElapsed}/{daysTotal} {t('frugal.execution.days')}</div>
          </div>
        </div>
        <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${(daysElapsed / daysTotal) * 100}%` }} />
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-3">
        {displayCategories.map((cat, idx) => (
          <CategoryRow
            key={cat.category}
            cat={cat}
            isExpanded={expandedCategory === cat.category}
            onToggle={toggleCategory}
            transactions={categoryTransactions[cat.category]}
            loadingTransactions={loadingTransactions}
            categoryColor={CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.default}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

export default ExecutionTab;
